import React, { useEffect, useMemo } from "react";
import { DataGrid, GridColDef, GridRowId } from "@mui/x-data-grid";
import { Box, Button, IconButton, Tooltip, Select, MenuItem, Typography, TextField, Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment, useMediaQuery } from "@mui/material";
import { MobileNumberField } from './MobileNumberField';
import { useTheme } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Expense } from "../utils/interface";
import { TextInputCell } from "./TextInputCell";
import { NumberDecimalInputCell } from "./NumberDecimalInputCell";
import { colors, typography } from "../theme";

export const RetailExpensesIndustrial = ({
  expenses,
  setExpenses,
  step,
  totalRetailSF,
  retailIncome,
  spaceType = 'Retail',
}: {
  expenses: Expense[];
  setExpenses: React.Dispatch<React.SetStateAction<Expense[]>>;
  step: string;
  totalRetailSF: number;
  retailIncome?: any[];
  spaceType?: string;
}) => {
  const expenseType = "Retail";

  // Only show expenses for this type (case-insensitive)
  const filteredExpenses = expenses.filter((exp) => (exp.type || '').toLowerCase() === expenseType.toLowerCase());

  // Ensure retail expenses have a stable id (for grid updates) if API omitted it
  useEffect(() => {
    let changed = false;
    const updated = expenses.map((row, idx) => {
      if ((row.type || '').toLowerCase() !== expenseType.toLowerCase()) return row;
      if (!row.id) {
        changed = true;
        return { ...row, id: `${Date.now()}-${idx}` };
      }
      return row;
    });
    if (changed) setExpenses(updated);
  }, [expenses, setExpenses]);

  const handleCellChange = (id: string | number, field: string, value: string | number) => {
    setExpenses((prev) =>
      prev.map((row) => (String(row.id) === String(id) ? { ...row, [field]: value } : row))
    );
    
  };

  const addRow = () => {
    const newRow: Expense = {
      id: String(Date.now()),
      name: "",
      factor: "per SF / Yr.",
      cost_per: 0,
      statistic: null,
      type: expenseType,
      start_month: 0,
      end_month: 0,
      rent_type_included: "Both",
    };
    setExpenses((prev) => [...prev, newRow]);
  };

  const deleteRow = (id: GridRowId) => {
    try {
      if (typeof window !== 'undefined' && (document as any)?.activeElement) {
        (document as any).activeElement?.blur?.();
      }
    } catch {}
    requestAnimationFrame(() => {
    setExpenses((prev) => prev.filter((row) => row.id !== id));
    });
  };

  // Flexible widths so columns fill the grid
  const flexByField: Record<string, number> = {
    name: 1.4,
    expense_growth: 0.9,
    factor: 1.1,
    cost_per: 1,
    statistic: 1,
    rent_type_included: 1,
    annual: 1,
    delete: 0.4,
  };

  // Sum of annual base rent from retail income (rent_per_square_foot_per_year * square_feet)
  const baseRentAnnual = useMemo(() => {
    const rows = Array.isArray(retailIncome) ? retailIncome : [];
    return rows.reduce((sum: number, r: any) => {
      const psf = Number(r?.rent_per_square_foot_per_year || 0);
      const sf = Number(r?.square_feet || 0);
      return sum + psf * sf;
    }, 0);
  }, [retailIncome]);

  const rows = useMemo(() => {
    return filteredExpenses.map((r) => ({
      ...r,
      id: String((r as any).id ?? ''), // enforce string id for grid stability
      rent_type_included: (r as any).rent_type_included ?? undefined,
    }));
  }, [filteredExpenses]);

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: "name",
        headerName: "Name",
        flex: flexByField.name,
        minWidth: 160,
        editable: false,
        renderCell: (params: any) => <TextInputCell params={params} handleCellChange={handleCellChange} field="name" />,
      },
      // {
      //   field: "expense_growth",
      //   headerName: "Expense Growth",
      //   flex: flexByField.expense_growth,
      //   minWidth: 110,
      //   sortable: false,
      //   cellClassName: 'greyed-out-cell',
      //   renderCell: () => <span>2.0%</span>,
      // },
      {
        field: "factor",
        headerName: "Factor",
        flex: flexByField.factor,
        minWidth: 160,
        sortable: false,
        editable: false,
        renderCell: (params: any) => (
          <Select
            size="small"
            fullWidth
            value={(params.value as string) || "Annual"}
            onChange={(e) => handleCellChange(params.id as string, "factor", e.target.value as string)}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            variant="standard"
            disableUnderline
            sx={{
              background: 'transparent',
              fontFamily: typography.fontFamily,
              fontSize: 14.5,
              fontWeight: 600,
              color: colors.grey[900],
              '& .MuiSelect-select': { textAlign: 'left', py: 0.5 },
            }}
          >
            <MenuItem value="Annual">Annual</MenuItem>
            <MenuItem value="Percent of Base Rent">Percent of Base Rent</MenuItem>
            <MenuItem value="per SF / Yr.">per SF / Yr.</MenuItem>
          </Select>
        ),
      },
      {
        field: "cost_per",
        headerName: "Expense",
        flex: flexByField.cost_per,
        minWidth: 130,
        editable: false,
        type: "number",
        renderCell: (params: any) => {
          const factor = String(params?.row?.factor || "Annual");
          const prefix = factor === "Percent of Base Rent" ? "" : "$";
          const suffix = factor === "Percent of Base Rent" ? "%" : "";
          return (
          <NumberDecimalInputCell
            params={params}
            handleCellChange={handleCellChange}
            field="cost_per"
              prefix={prefix}
              suffix={suffix}
            />
          );
        },
      },
      {
        field: "statistic",
        headerName: "Statistic",
        flex: flexByField.statistic,
        minWidth: 150,
        sortable: false,
        cellClassName: 'greyed-out-cell',
        renderCell: (params: any) => {
          const factor = String(params?.row?.factor || "Annual");
          if (factor === "per SF / Yr.") {
            return <span>{Number(totalRetailSF || 0).toLocaleString()} SF</span>;
          }
          if (factor === "Percent of Base Rent") {
            return <span>${Number(baseRentAnnual || 0).toLocaleString()}</span>;
          }
          return <span>-</span>;
        },
      },
      {
        field: "rent_type_included",
        headerName: "Rent Type Included",
        flex: flexByField.rent_type_included,
        minWidth: 140,
        editable: false,
        renderCell: (params: any) => (
          <Select
            size="small"
            fullWidth
            value={params.value ?? "Both"}
            onChange={(e) => handleCellChange(params.id as string, "rent_type_included", e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            displayEmpty
            variant="standard"
            disableUnderline
            sx={{
              background: 'transparent',
              fontFamily: typography.fontFamily,
              fontSize: 14.5,
              fontWeight: 600,
              color: colors.grey[900],
              '& .MuiSelect-select': { textAlign: 'left', py: 0.5 },
            }}
          >
            <MenuItem value="Both">Both</MenuItem>
            <MenuItem value="Gross">Gross</MenuItem>
            <MenuItem value="NNN">NNN</MenuItem>
            <MenuItem value="Neither">Neither</MenuItem>
          </Select>
        ),
      },
      {
        field: "annual",
        headerName: "Annual",
        flex: flexByField.annual,
        minWidth: 110,
        editable: false,
        renderCell: (params: any) => {
          const factor = String(params?.row?.factor || "Annual");
          const cost = Number(params?.row?.cost_per || 0);
          let annual = 0;
          if (factor === "Annual") {
            annual = cost;
          } else if (factor === "per SF / Yr.") {
            annual = cost * Number(totalRetailSF || 0);
          } else if (factor === "Percent of Base Rent") {
            annual = (cost / 100) * Number(baseRentAnnual || 0);
          }
          return <span style={{ color: colors.grey[600] }}>${Number(annual).toLocaleString()}</span>;
        },
      },
      {
        field: "delete",
        headerName: "",
        flex: flexByField.delete,
        minWidth: 70,
        sortable: false,
        renderHeader: () => <span />,
        renderCell: (params: any) => (
          <span className="u-row-action">
            <Tooltip title="Delete">
              <span>
                <IconButton onClick={() => deleteRow(params.id)} color="error" size="small">
                  <DeleteIcon />
                </IconButton>
              </span>
            </Tooltip>
          </span>
        ),
      },
    ],
    [expenses, totalRetailSF]
  );

  const getRowClassName = (params: any) =>
    params.indexRelativeToCurrentPage % 2 === 0 ? 'u-row-odd' : 'u-row-even';

  const CustomFooter = () => {
    const totalAnnual = (filteredExpenses || []).reduce((sum, row: any) => {
      const factor = String(row?.factor || "Annual");
      const cost = Number(row?.cost_per || 0);
      if (factor === "Annual") return sum + cost;
      if (factor === "per SF / Yr.") return sum + cost * Number(totalRetailSF || 0);
      if (factor === "Percent of Base Rent") return sum + (cost / 100) * Number(baseRentAnnual || 0);
      return sum;
    }, 0);
    return (
      <Box
        sx={{
          p: 2,
          backgroundColor: colors.white,
          borderTop: `1px solid ${colors.grey[300]}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          fontSize: 14.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button variant="contained" size="small" onClick={addRow} sx={{ whiteSpace: 'nowrap', minWidth: 200 }}>
            Add Expense
          </Button>
        </Box>
        <Box sx={{ display: 'flex', gap: 3, justifyContent: 'flex-end', width: '100%', color: colors.grey[700] }}>
          <Box sx={{ textAlign: 'right', fontWeight: 600 }}>
            Total Expenses:{' '}
            <Box component="span" sx={{ fontWeight: 700, color: colors.grey[900] }}>
              ${totalAnnual.toLocaleString()}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };


  

  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));
  const [editId, setEditId] = React.useState<string | null>(null);
  const editingRow: any = editId ? filteredExpenses.find((r: any) => String(r.id) === editId) ?? null : null;

  const computeAnnualFor = (row: any) => {
    const factor = String(row?.factor || "Annual");
    const cost = Number(row?.cost_per || 0);
    if (factor === "Annual") return cost;
    if (factor === "per SF / Yr.") return cost * Number(totalRetailSF || 0);
    if (factor === "Percent of Base Rent") return (cost / 100) * Number(baseRentAnnual || 0);
    return 0;
  };

  if (isMobile) {
    const totalAnnual = (filteredExpenses || []).reduce((sum: number, r: any) => sum + computeAnnualFor(r), 0);
    const editIsPct = String(editingRow?.factor || "") === "Percent of Base Rent";
    return (
      <Box sx={{ mt: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {rows.map((row: any) => {
            const isPct = String(row?.factor || "") === "Percent of Base Rent";
            const valueDisplay = isPct ? `${row.cost_per || 0}%` : `$${Number(row.cost_per || 0).toLocaleString()}`;
            const annual = computeAnnualFor(row);
            return (
              <Box key={row.id} sx={{ border: "1px solid #e5e7eb", borderRadius: 2, px: 1.5, py: 1.25, bgcolor: "#fff", display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontWeight: 700, color: "#1f2937", fontSize: "0.9rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {row.name || "Untitled"}
                  </Typography>
                  <Typography sx={{ color: "#6b7280", fontSize: "0.72rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {row.factor} · {valueDisplay} · {row.rent_type_included || "Both"}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                  <Typography sx={{ fontWeight: 600, color: "#1f2937", fontSize: "0.85rem" }}>${annual.toLocaleString(undefined, { maximumFractionDigits: 0 })}</Typography>
                  <Typography sx={{ color: "#6b7280", fontSize: "0.65rem" }}>/ yr</Typography>
                </Box>
                <Box sx={{ display: "flex", flexShrink: 0 }}>
                  <Tooltip title="Edit"><IconButton size="small" color="primary" onClick={() => setEditId(String(row.id))} sx={{ p: 0.5 }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Delete"><span><IconButton size="small" color="error" onClick={() => deleteRow(row.id)} sx={{ p: 0.5 }}><DeleteIcon fontSize="small" /></IconButton></span></Tooltip>
                </Box>
              </Box>
            );
          })}
        </Box>

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1, mt: 2 }}>
          <Button variant="contained" size="small" onClick={addRow}>Add Expense</Button>
          <Box sx={{ textAlign: "right", fontSize: 13, color: "#1f2937", ml: "auto", fontWeight: 600 }}>
            Total Expenses: ${totalAnnual.toLocaleString()}
          </Box>
        </Box>

        <Dialog open={!!editingRow} onClose={() => setEditId(null)} fullWidth maxWidth="xs">
          <DialogTitle>Edit {editingRow?.name || "Expense"}</DialogTitle>
          <DialogContent>
            {editingRow && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                <TextField label="Name" size="small" fullWidth value={editingRow.name ?? ""}
                  onChange={(e) => handleCellChange(editingRow.id, "name", e.target.value)} />
                <TextField select label="Factor" size="small" fullWidth value={editingRow.factor ?? "Annual"}
                  onChange={(e) => handleCellChange(editingRow.id, "factor", e.target.value)}>
                  <MenuItem value="Annual">Annual</MenuItem>
                  <MenuItem value="Percent of Base Rent">Percent of Base Rent</MenuItem>
                  <MenuItem value="per SF / Yr.">per SF / Yr.</MenuItem>
                </TextField>
                <MobileNumberField label="Expense" type="number" size="small" fullWidth value={editingRow.cost_per ?? ""}
                  onChange={(e) => handleCellChange(editingRow.id, "cost_per", Number(e.target.value))}
                  InputProps={editIsPct
                    ? { endAdornment: <InputAdornment position="end">%</InputAdornment> }
                    : { startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
                <TextField select label="Rent Type Included" size="small" fullWidth value={editingRow.rent_type_included ?? "Both"}
                  onChange={(e) => handleCellChange(editingRow.id, "rent_type_included", e.target.value)}>
                  <MenuItem value="Both">Both</MenuItem>
                  <MenuItem value="Gross">Gross</MenuItem>
                  <MenuItem value="NNN">NNN</MenuItem>
                  <MenuItem value="Neither">Neither</MenuItem>
                </TextField>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditId(null)} variant="contained">Done</Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ mb: 2 }}>
          <DataGrid
            disableColumnMenu
            disableColumnFilter
            disableColumnSelector
            disableColumnSorting
            rows={rows}
            columns={columns}
            disableRowSelectionOnClick
            getRowId={(row) => String((row as any).id)}
            slots={{ footer: CustomFooter }}
            rowHeight={52}
            getRowClassName={getRowClassName}
            hideFooterSelectedRowCount
            sx={{
              background: colors.white,
              minWidth: '900px',
              border: `1px solid ${colors.grey[300]}`,
              borderRadius: 2,
              fontFamily: typography.fontFamily,
              fontSize: 14.5,
              '& .MuiDataGrid-cell, & .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-cellContent': {
                fontFamily: typography.fontFamily,
                fontSize: 14.5,
              },
              '& .MuiDataGrid-main': { background: colors.white },
              '& .MuiDataGrid-columnHeaders': { background: colors.white, minHeight: 52, maxHeight: 52, borderBottom: `1px solid ${colors.grey[300]}` },
              '& .MuiDataGrid-columnHeader': { background: colors.white, minHeight: 52, maxHeight: 52 },
              '& .MuiDataGrid-columnHeaderTitleContainer': { background: colors.white },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 600,
                fontSize: 14.5,
                fontFamily: typography.fontFamily,
                textTransform: 'none',
                lineHeight: '52px',
                color: colors.grey[900],
              },
              '& .MuiDataGrid-cell': { borderBottom: `1px solid ${colors.grey[300]}`, background: colors.white, fontSize: 14.5, color: colors.grey[900] },
              '& .greyed-out-cell': { color: colors.grey[600] },
              // Hover underline for editable inputs
              '& .u-editable-input': { border: 'none', borderBottom: '2px solid transparent', borderRadius: 0, background: 'transparent', fontWeight: 600 },
              '& .MuiDataGrid-row:hover .u-editable-input, & .u-editable-input:focus': { borderBottom: `2px solid ${colors.blue} !important` },
              '& .MuiDataGrid-row': { background: colors.white },
              '& .u-row-even': { background: colors.grey[50] },
              '& .MuiDataGrid-row:hover': { backgroundColor: colors.blueTint },
              '& .u-row-action': {
                opacity: 0,
                visibility: 'hidden',
                pointerEvents: 'none',
                transition: 'opacity 120ms ease'
              },
              '& .MuiDataGrid-row:hover .u-row-action, & .MuiDataGrid-cell:focus-within .u-row-action': {
                opacity: 1,
                visibility: 'visible',
                pointerEvents: 'auto'
              },
              '& .MuiDataGrid-virtualScroller': { background: colors.white },
              '& .MuiDataGrid-footerContainer': { background: colors.white }
            }}
          />
      </Box>
    </Box>
  );
};

